import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Divider,
  Dropdown,
  Input,
  Modal,
  Space,
  Typography,
} from 'antd';
import React, { useCallback } from 'react';
import { Control, get, useController, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useMarketplaceProperties } from '../hooks/useMarketplaceOptions';
import { emptyOfferBlock } from '../schemas/configurationSchema';
import {
  ACQUISITION_LABELS,
  AcquisitionType,
  AUDIENCE_TARGET_MAP,
  TargetMarket,
} from '../types/types';
import { baseProps } from '../utils/modalUtils';
import { FormItem } from './FormItem';
import { OfferBlock } from './OfferBlock';

const { Text } = Typography;

interface AcquisitionTypeCardProps {
  type: AcquisitionType;
  namePrefix: string;
  control: Control<any>;
  audienceKey: TargetMarket;
  onRemove: (type: AcquisitionType) => void;
}

const MAX_OFFERS = 5;
const isMultiOffer = (type: AcquisitionType) =>
  type === AcquisitionType.Subscription || type === AcquisitionType.Rental;

export const AcquisitionTypeCard: React.FC<AcquisitionTypeCardProps> = ({
  type,
  namePrefix,
  control,
  audienceKey,
  onRemove,
}) => {
  const label = ACQUISITION_LABELS[type];
  const multi = isMultiOffer(type);

  const { properties } = useMarketplaceProperties();

  const audienceTypeId = properties?.types.find(
    t => t.code === AUDIENCE_TARGET_MAP[audienceKey]
  )?.id;

  const acquisitionId = properties?.acquisitionTypes.find(
    a => a.code === type
  )?.id;

  const {
    formState: { errors },
  } = useFormContext();

  const { field: offersField } = useController({
    control,
    name: `${namePrefix}.offers`,
    disabled: !multi,
  });

  const offers: any[] = multi ? (offersField.value ?? []) : [];

  const handleAddOffer = useCallback(() => {
    if (offers.length >= MAX_OFFERS) return;
    offersField.onChange([...offers, emptyOfferBlock]);
  }, [offers, offersField]);

  const handleRemoveOffer = useCallback(
    (index: number) => {
      Modal.confirm({
        title: `Remover oferta ${index + 1}`,
        icon: <ExclamationCircleOutlined />,
        content: `Tem certeza que deseja remover a oferta ${index + 1}? Esta ação não pode ser desfeita.`,
        okText: 'Remover',
        okButtonProps: { danger: true },
        cancelText: 'Cancelar',
        onOk: () => {
          offersField.onChange(offers.filter((_, i) => i !== index));
        },
        ...baseProps,
      });
    },
    [offers, offersField]
  );

  const handleRemoveType = useCallback(() => {
    Modal.confirm({
      title: `Remover ${label}`,
      icon: <ExclamationCircleOutlined />,
      content: `Tem certeza que deseja remover todas as ofertas de ${label}? Essa ação não poderá ser desfeita.`,
      okText: 'Remover',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => onRemove(type),
      ...baseProps,
    });
  }, [label, onRemove, type]);

  const menuItems = [
    {
      key: 'remove',
      label: 'Remover',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleRemoveType,
    },
  ];

  return (
    <CardWrapper>
      <CardHeader>
        <Text strong style={{ fontSize: 15 }}>
          {label}
        </Text>
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement='bottomRight'
        >
          <Button
            type='text'
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
            size='small'
          />
        </Dropdown>
      </CardHeader>

      <CardBody>
        <FormItem
          label='Título'
          required
          control={control}
          name={`${namePrefix}.title`}
          renderItem={({ field }) => (
            <Input
              {...field}
              placeholder={`ex: ${label} e tenha acesso`}
              status={get(errors, `${namePrefix}.title`) ? 'error' : undefined}
            />
          )}
        />

        <FormItem
          label='Subtítulo'
          required
          control={control}
          name={`${namePrefix}.subtitle`}
          renderItem={({ field }) => (
            <Input.TextArea
              {...field}
              placeholder={`Descrição da oferta de ${label.toLowerCase()}.`}
              autoSize={{ minRows: 2, maxRows: 4 }}
              status={
                get(errors, `${namePrefix}.subtitle`) ? 'error' : undefined
              }
            />
          )}
        />

        <Divider size='small' />

        {!multi && (
          <OfferBlock
            namePrefix={namePrefix}
            control={control}
            typeLabel={label}
            audienceTypeId={audienceTypeId}
            acquisitionId={acquisitionId}
            acquisitionType={type}
          />
        )}

        {multi && (
          <Space direction='vertical' size={12} style={{ width: '100%' }}>
            {offers.map((_offer, index) => (
              <OfferBlock
                key={index}
                namePrefix={`${namePrefix}.offers.${index}`}
                control={control}
                typeLabel={label}
                audienceTypeId={audienceTypeId}
                acquisitionId={acquisitionId}
                acquisitionType={type}
                index={index}
                onRemove={
                  offers.length > 1 ? () => handleRemoveOffer(index) : undefined
                }
              />
            ))}

            {offers.length < MAX_OFFERS && (
              <Button
                type='link'
                icon={<PlusOutlined />}
                onClick={handleAddOffer}
                style={{ width: '100%' }}
              >
                Adicionar {label}
              </Button>
            )}
          </Space>
        )}
      </CardBody>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  border: 1px solid #303030;
  border-radius: 8px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #303030;
`;

const CardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
